import { TradeInUI } from "./TradeInUI";
import { StandardProps } from "@/components/componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { dispatchAnalyticsEvent } from "@/utils";
import { getDefaultCategory } from "@/utils/product/getDefaultCategory";
import { Message, Product, Variant } from "@/types/types";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { isOutlet } from "@/utils/variant/isOutlet";
import { TradeInContextProvider } from "./TradeInContext";

enum TRADE_IN_OPTION {
  MICROSITE,
  INTEGRATED,
}

export const TradeInEvents = {
  PW_TRADE_IN_FIND_YOUR_DEVICE_STEP_CTA:
    "PW_TRADE_IN_FIND_YOUR_DEVICE_STEP_CTA",
  PW_TRADE_IN_SEARCH_STEP_DROP_OUT: "PW_TRADE_IN_SEARCH_STEP_DROP_OUT",
  SL_TRADE_IN_LAUNCH_INTEGRATED: "SL_TRADE_IN_LAUNCH_INTEGRATED",
  SL_TRADE_IN_LAUNCH_STANDALONE: "SL_TRADE_IN_LAUNCH_STANDALONE",
  SL_TRADE_IN_LAUNCH_STANDALONE_IN_STOCK:
    "SL_TRADE_IN_LAUNCH_STANDALONE_IN_STOCK",
  PW_TRADE_IN_ADDED_QUOTE_TO_BASKET: "PW_TRADE_IN_ADDED_QUOTE_TO_BASKET",
  TRADE_IN_ADD_TO_BASKET: "TRADE_IN_ADD_TO_BASKET",
  TRADE_IN_QUOTE_SUMMARY: "TRADE_IN_QUOTE_SUMMARY",
  PW_TRADE_IN_INFO_ABOUT_YOUR_DEVICE_STEP_CTA:
    "PW_TRADE_IN_INFO_ABOUT_YOUR_DEVICE_STEP_CTA",
  PW_TRADE_IN_YOUR_PAYOUT_OPTIONS_STEP_CTA:
    "PW_TRADE_IN_YOUR_PAYOUT_OPTIONS_STEP_CTA",
  TRADE_IN_READ_MORE_EVENT: "TRADE_IN_READ_MORE_EVENT",
  TRADE_IN_SPECIAL_OFFERS_DRAWER_EVENT: "TRADE_IN_SPECIAL_OFFERS_DRAWER_EVENT",
};

export function getMessages(
  selectedVariant: Variant,
  product: Product,
): Message[] {
  return (
    (selectedVariant.messaging?.length
      ? selectedVariant.messaging
      : product.messaging) || []
  );
}

export function TradeIn({
  runtimeConfig,
  product,
  configProps,
  flags,
}: Pick<StandardProps, "product" | "runtimeConfig" | "configProps" | "flags">) {
  const { eligibleForTradeIn } = product;

  const {
    variant,
    variant: { availability },
  } = useVariant(product);

  const tradeInOptions = [TRADE_IN_OPTION.MICROSITE];
  if (
    availability?.availableToOrder &&
    !availability?.shipFromStore &&
    !isOutlet(variant)
  ) {
    tradeInOptions.push(TRADE_IN_OPTION.INTEGRATED);
  }

  const getQuotesFromBasket = configProps?.getQuotesFromBasket === true;
  const isFullWidthMode = configProps?.isFullWidthMode === true;

  if (eligibleForTradeIn) {
    const hasMultipleTradeInRoutes = tradeInOptions.length > 1;
    const messages = getMessages(variant, product);

    return (
      <TradeInContextProvider
        value={{
          productImage: variant.images?.primary,
          messages,
          micrositeUrl: runtimeConfig.tradeInMicrositeUrl,
          tradeInBaseUrl: runtimeConfig.tradeInBaseUrl,
          basketApiBaseUrl: runtimeConfig.basketApiBaseUrl,
          productId: product.aliases.pimProductId,
          getQuotesFromBasket: getQuotesFromBasket,
          micrositeOnClick: () =>
            dispatchGetStartedAnalytics(
              product,
              variant,
              hasMultipleTradeInRoutes,
            ),
          flags,
        }}
      >
        <TradeInUI
          isFullWidthMode={isFullWidthMode}
          dispatchTradeInStartedAnalytics={() =>
            dispatchTradeInStartedAnalytics(product, variant)
          }
          hasMultipleTradeInRoutes={hasMultipleTradeInRoutes}
        />
      </TradeInContextProvider>
    );
  }
  return null;
}

function getAnalyticsPayload(product: Product, selectedVariant: Variant) {
  return {
    type: "pdp",
    additionalInfo: {},
    properties: {},
    products: [
      {
        // why does the compiler think brand may be undefined here? It's mandatory on product
        brand: product.brand?.name,
        category: getDefaultCategory(product),
        price: selectedVariant.price?.value,
        productName: selectedVariant.title,
        productId: product.aliases.pimProductId,
        sku: selectedVariant.aliases.skuId,
      },
    ],
  };
}

function dispatchTradeInStartedAnalytics(
  product: Product,
  selectedVariant: Variant,
) {
  dispatchMonetateQEvent(TradeInEvents.SL_TRADE_IN_LAUNCH_INTEGRATED);

  dispatchAnalyticsEvent(
    "global.interaction",
    {
      ...getAnalyticsPayload(product, selectedVariant),
      subType: "trade in get started",
    },
    {},
  );
}

function dispatchGetStartedAnalytics(
  product: Product,
  selectedVariant: Variant,
  hasMultipleTradeInRoutes: boolean,
) {
  dispatchMonetateQEvent(
    hasMultipleTradeInRoutes
      ? TradeInEvents.SL_TRADE_IN_LAUNCH_STANDALONE_IN_STOCK
      : TradeInEvents.SL_TRADE_IN_LAUNCH_STANDALONE,
  );
  dispatchAnalyticsEvent(
    "global.interaction",
    {
      ...getAnalyticsPayload(product, selectedVariant),
      subType: "trade in get started - microsite",
    },
    {},
  );
}
